import jwtDecode from 'jwt-decode';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { PageLoader } from 'src/components/shared/Loader/PageLoader';
import { UserApi } from 'src/data/api/users/users.api';
import { UserRole } from 'src/data/models/user';
import { Storage } from 'src/utils/storage';
import {
  clearTokens,
  initializeKeycloak,
  getKeycloak,
  setTokens
} from '../../components/pages/Auth/keycloak.config';
import { AuthorizationError } from '../../components/pages/Error/AuthorizationError';
import { LoginError } from '../../components/pages/Error/LoginError';
import { Language, LANGUAGE_KEY } from 'src/i18n';
import { loadCurrentBVTSettings } from 'src/utils/Smartbuss2Config';
import { UserDto } from 'src/data/models/v2/user/userDto';


type Token = {
  iat: number;
  auth_time: number;
  preferred_username: string;
  roles: UserRole[];
};


export type AuthContextProps = {
  user: UserDto;
  userRoles: UserRole[];
  isAuthenticated: boolean;
  language: Language;
  signin: () => void;
  signout: () => void;
  setCurrentLanguage: (language: Language) => void;
};

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps
);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [user, setUser] = useState<UserDto>({} as UserDto);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [language, setLanguage] = useState<Language>(Language.English);

  useEffect(() => {
    const setCurrentUser = async (roles: UserRole[]) => {
      const currentUser = await UserApi.current();
      if (currentUser) {
        setUser(currentUser);
        setUserRoles(roles);
      }
      setIsAuthenticating(false);
      setLanguage(Storage.getItem(LANGUAGE_KEY) ?? Language.English);
    };

    setIsAuthenticating(true);
    initializeKeycloak(isAuthenticated => {
      const keycloak = getKeycloak();
      if (isAuthenticated && keycloak.token && keycloak.refreshToken) {
        const token = jwtDecode<Token>(keycloak.token);
        setTokens(keycloak.token, keycloak.refreshToken);

        if (token.roles && token.roles.length > 0) {
          setCurrentUser(token.roles);
        } else {
          setCurrentUser(['user']);
        }

        loadCurrentBVTSettings();
      }
    });
  }, []);

  const signout = () => {
    clearTokens();
    const keycloak = getKeycloak();
    keycloak.logout();
  };

  const signin = () => {
    const keycloak = getKeycloak();
    keycloak.login();
  };

  const setCurrentLanguage = (language: Language) => {
    Storage.setItem(LANGUAGE_KEY, language);
    setLanguage(language);
  };

  const isAuthenticated = !!user?.id;
  const hasRoles = userRoles.length > 0;

  return (
    <AuthContext.Provider
      value={{
        user: user,
        userRoles: userRoles,
        isAuthenticated: isAuthenticated,
        language: language,
        signin: signin,
        signout: signout,
        setCurrentLanguage: setCurrentLanguage
      }}
    >
      {isAuthenticating && <PageLoader global />}
      {!isAuthenticated && !isAuthenticating && <LoginError />}
      {isAuthenticated && !hasRoles && <AuthorizationError />}
      {isAuthenticated && hasRoles && children}
    </AuthContext.Provider>
  );
};
